import { Component, OnInit, Input } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';
import { JavapiService } from '../../services/javapi.service';
import { Metodosui } from '../../services/metodosui';
import { MetodosgenService } from '../../services/metodosgen.service';
import { AdminCrudPage } from '../../pages/ajustes/admin-crud/admin-crud.page';
import { ComponentesGenericosComponent } from '../componentes-genericos/componentes-genericos.component';

@Component({
  selector: 'app-inspecdatosareaestimadoedit',
  templateUrl: './inspecdatosareaestimadoedit.component.html',
  styleUrls: ['./inspecdatosareaestimadoedit.component.scss'],
  standalone: false
})
export class InspecdatosareaestimadoeditComponent implements OnInit {

  @Input() areaId: string = '';
  @Input() estimado: any = null;
  @Input() defaultTipo: string = 'PLANILLA';

  current: any = {
    id: '0',
    area_id: '',
    tipo: 'PLANILLA',
    dias: 1,
    trabajadores: 1,
    turno: 'DIURNO',
    monto_diario: 35,
    cantidad: 1,
    costo: null,
    subcontratista: '',
    comentario: ''
  };

  loading: boolean = false;
  isEdit: boolean = false;

  // Autocomplete Subcontratista
  subcontratistasList: any[] = [];
  filteredSubcontratistas: any[] = [];
  showSubDropdown: boolean = false;
  isSubRegistrado: boolean = false;

  // Planilla + Movilidad vinculada
  addMovilidad: boolean = false;
  montoMovilidad: number = 20;

  constructor(
    private modalCtrl: ModalController,
    private javaService: JavapiService,
    private ui: Metodosui,
    private metodosGen: MetodosgenService
  ) { }

  ngOnInit() {
    this.current.area_id = String(this.areaId || '');
    if (this.estimado) {
      this.isEdit = true;
      const tipo = (this.estimado.tipo || 'GENERAL').toUpperCase();
      const cantidad = Number(this.estimado.cantidad) || 1;
      let costo = Number(this.estimado.costo) || 0;
      if (tipo === 'MATERIAL' && cantidad > 0) {
        costo = Math.round((costo / cantidad) * 100) / 100;
      }
      this.current = {
        ...this.estimado,
        tipo,
        dias: Number(this.estimado.dias) || 1,
        trabajadores: Number(this.estimado.trabajadores) || 1,
        monto_diario: Number(this.estimado.monto_diario) || 0,
        costo: costo > 0 ? costo : null,
        cantidad,
        turno: this.estimado.turno || 'DIURNO'
      };
      if (this.current.subcontratista) {
        this.isSubRegistrado = true;
      }
      this.addMovilidad = false;
    } else {
      this.isEdit = false;
      this.current.tipo = (this.defaultTipo || 'PLANILLA').toUpperCase();
      this.initDefaultsForTipo(this.current.tipo);
    }

    if (this.current.tipo === 'SUBCONTRATO') {
      this.loadSubcontratistas();
    }
  }

  initDefaultsForTipo(tipo: string) {
    if (tipo === 'PLANILLA') {
      this.current.dias = 1;
      this.current.trabajadores = 1;
      this.current.turno = 'DIURNO';
      this.current.monto_diario = 35;
      this.addMovilidad = true;
      this.montoMovilidad = 20;
    } else if (tipo === 'MOVILIDAD') {
      this.current.dias = 1;
      this.current.monto_diario = 20;
    } else if (tipo === 'MATERIAL') {
      this.current.cantidad = 1;
      this.current.costo = null;
    } else if (tipo === 'SUBCONTRATO') {
      this.current.costo = null;
      this.loadSubcontratistas();
    }
  }

  onTurnoChange() {
    if (this.current.tipo === 'PLANILLA') {
      this.current.monto_diario = this.current.turno === 'DIURNO' ? 35 : 40;
    }
  }

  async openTurnoSelector() {
    const turnos = [
      { id: 'DIURNO', nombre: 'Diurno' },
      { id: 'NOCTURNO', nombre: 'Nocturno' }
    ];

    const modal = await this.modalCtrl.create({
      component: ComponentesGenericosComponent,
      componentProps: {
        entity: 'TURNO',
        title: 'Seleccionar Turno',
        placeholder: 'Buscar turno...',
        items: turnos,
        initialSearch: '',
        selectedIds: this.current.turno ? [this.current.turno] : []
      }
    });

    await modal.present();
    const { data } = await modal.onWillDismiss();

    if (data && data.selected) {
      this.current.turno = data.selected.id;
      this.onTurnoChange();
    }
  }

  getTipoLabel(): string {
    switch (this.current.tipo) {
      case 'PLANILLA': return 'Planilla';
      case 'MATERIAL': return 'Material';
      case 'MOVILIDAD': return 'Movilidad';
      case 'SUBCONTRATO': return 'Subcontrato';
      case 'GENERAL':
      case 'NOTA': return 'Nota';
      default: return 'Estimado';
    }
  }

  getTipoIcon(): string {
    switch (this.current.tipo) {
      case 'PLANILLA': return 'people-outline';
      case 'MATERIAL': return 'cube-outline';
      case 'MOVILIDAD': return 'flashlight-outline';
      case 'SUBCONTRATO': return 'business-outline';
      case 'GENERAL':
      case 'NOTA': return 'document-text-outline';
      default: return 'pricetag-outline';
    }
  }

  dismiss(saved: boolean = false) {
    this.modalCtrl.dismiss(saved);
  }

  async loadSubcontratistas() {
    try {
      const data: any = await firstValueFrom(
        this.javaService.getCommonModel(
          this.metodosGen.setModeloGeneral('selDtsList', 'trSubcontratistas', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '')
        )
      );
      if (Array.isArray(data)) {
        this.subcontratistasList = data.map(c => ({ id: c.dt1, nombre: c.dt2 }));
      }
    } catch (e) {
      console.error('Error loading subcontratistas', e);
    }
  }

  async openSubcontratistaSelector() {
    if (this.subcontratistasList.length === 0) {
      await this.loadSubcontratistas();
    }

    const currentSub = this.subcontratistasList.find(s => s.nombre.toLowerCase().trim() === (this.current.subcontratista || '').toLowerCase().trim());
    const selectedIds = currentSub ? [Number(currentSub.id)] : [];

    const modal = await this.modalCtrl.create({
      component: ComponentesGenericosComponent,
      componentProps: {
        entity: 'SUBCONTRATISTA',
        title: 'Seleccionar Subcontratista',
        placeholder: 'Buscar subcontratista o empresa...',
        items: this.subcontratistasList,
        initialSearch: this.current.subcontratista || '',
        selectedIds: selectedIds
      }
    });

    await modal.present();
    const { data } = await modal.onWillDismiss();

    if (data && data.selected) {
      this.current.subcontratista = data.selected.nombre;
      this.isSubRegistrado = true;
      if (data.selected.isNew) {
        await this.loadSubcontratistas();
      }
    }
  }

  selectSubcontratista(s: any) {
    this.current.subcontratista = s.nombre;
    this.isSubRegistrado = true;
  }

  clearSubcontratista(event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    this.current.subcontratista = '';
    this.isSubRegistrado = false;
  }

  async save() {
    if (this.current.tipo === 'PLANILLA' && (this.current.dias <= 0 || this.current.trabajadores <= 0)) {
      this.ui.showToast('Ingrese días y trabajadores válidos', 'warning');
      return;
    }
    if (this.current.tipo === 'MATERIAL' && (this.current.cantidad <= 0 || !this.current.costo || Number(this.current.costo) <= 0)) {
      this.ui.showToast('Ingrese una cantidad y costo unitario válidos', 'warning');
      return;
    }
    if (this.current.tipo === 'MOVILIDAD' && (this.current.dias <= 0 || this.current.monto_diario <= 0)) {
      this.ui.showToast('Ingrese días y monto válidos', 'warning');
      return;
    }
    if (this.current.tipo === 'SUBCONTRATO') {
      if (!this.current.costo || Number(this.current.costo) <= 0) {
        this.ui.showToast('Ingrese el costo acordado del subcontrato', 'warning');
        return;
      }
      if (!this.current.subcontratista) {
        this.ui.showToast('Ingrese el subcontratista', 'warning');
        return;
      }
      if (!this.isSubRegistrado) {
        this.ui.showToast('Debe seleccionar un subcontratista de la lista o crearlo', 'warning');
        return;
      }
    }

    this.loading = true;
    try {
      let finalCost = Number(this.current.costo) || 0;
      const isInsert = this.current.id === '0' || !this.current.id;

      if (this.current.tipo === 'PLANILLA') {
        const dias = Number(this.current.dias) || 0;
        const trab = Number(this.current.trabajadores) || 0;
        const monto = Number(this.current.monto_diario) || 0;
        finalCost = dias * trab * monto;
      } else if (this.current.tipo === 'MATERIAL') {
        const cant = Number(this.current.cantidad) || 0;
        const precio = Number(this.current.costo) || 0;
        finalCost = cant * precio;
      } else if (this.current.tipo === 'MOVILIDAD') {
        const dias = Number(this.current.dias) || 0;
        const monto = Number(this.current.monto_diario) || 0;
        finalCost = dias * monto;
      }

      await this.ui.showLoadingPgv2('Guardando estimación...');

      // 1. Guardar registro principal
      const resp: any = await firstValueFrom(
        this.javaService.getCommonModel({
          empresa: 'pgarcia2',
          rutina: 'genUpdate',
          subrutina: 'saveEstimado',
          usuario: this.getUsuario(),
          datos: {
            dt1: isInsert ? 'INSERT' : 'UPDATE',
            dt2: String(this.current.id || '0'),
            dt3: String(this.current.area_id),
            dt4: this.current.tipo,
            dt5: String(this.current.dias || 0),
            dt6: String(this.current.trabajadores || 0),
            dt7: this.current.turno || 'DIURNO',
            dt8: String(this.current.monto_diario || 0),
            dt9: String(this.current.cantidad || 0),
            dt10: String(finalCost),
            dt11: this.current.subcontratista || '',
            dt12: this.current.comentario || '',
            dt13: this.getUsuario(),
            dt14: '0',
            dt15: ''
          }
        })
      );

      // 2. Manejo de movilidad vinculada automática (si aplica en inserción)
      if (this.current.tipo === 'PLANILLA' && this.addMovilidad && isInsert && resp && resp.length > 0) {
        const planillaId = resp[0].dt1;
        const mobCost = (Number(this.current.dias) || 0) * (Number(this.montoMovilidad) || 0);

        await firstValueFrom(
          this.javaService.getCommonModel({
            empresa: 'pgarcia2',
            rutina: 'genUpdate',
            subrutina: 'saveEstimado',
            usuario: this.getUsuario(),
            datos: {
              dt1: 'INSERT',
              dt2: '0',
              dt3: String(this.current.area_id),
              dt4: 'MOVILIDAD',
              dt5: String(this.current.dias),
              dt6: '0',
              dt7: '',
              dt8: String(this.montoMovilidad),
              dt9: String(this.current.dias),
              dt10: String(mobCost),
              dt11: '',
              dt12: 'Movilidad (Planilla #' + planillaId + ')',
              dt13: this.getUsuario(),
              dt14: String(planillaId),
              dt15: ''
            }
          })
        );
      }

      await this.ui.hideLoadingPgv2();
      this.ui.showToast('Estimado guardado correctamente', 'success');
      this.dismiss(true);
    } catch (error: any) {
      await this.ui.hideLoadingPgv2();
      this.ui.showToast('Error al guardar', 'danger');
      console.error('Error saving estimado', error);
    } finally {
      this.loading = false;
    }
  }

  async deleteItem() {
    const confirm = await this.ui.showConfirmationPgV2({
      header: 'Eliminar Estimado',
      subHeader: this.getTipoLabel(),
      message: `¿Estás seguro de eliminar este registro de ${this.getTipoLabel().toLowerCase()}?\n\n⚠️ Si tiene gastos asociados de movilidad vinculados también se eliminarán.`,
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      destructive: true
    });

    if (!confirm) return;

    this.loading = true;
    try {
      await this.ui.showLoadingPgv2('Eliminando...');
      await firstValueFrom(
        this.javaService.getCommonModel({
          empresa: 'pgarcia2',
          rutina: 'genUpdate',
          subrutina: 'saveEstimado',
          usuario: this.getUsuario(),
          datos: {
            dt1: 'DELETE',
            dt2: String(this.current.id),
            dt3: String(this.current.area_id),
            dt4: '', dt5: '', dt6: '', dt7: '', dt8: '', dt9: '', dt10: '', dt11: '', dt12: '',
            dt13: this.getUsuario(),
            dt14: '0', dt15: ''
          }
        })
      );
      await this.ui.hideLoadingPgv2();
      this.ui.showToast('Estimado eliminado con éxito', 'success');
      this.dismiss(true);
    } catch (error: any) {
      await this.ui.hideLoadingPgv2();
      console.error('Error deleting estimado', error);
      this.ui.showToast('Error al eliminar', 'danger');
    } finally {
      this.loading = false;
    }
  }

  private getUsuario(): string {
    const userJson = localStorage.getItem('user');
    if (userJson) {
      const user = JSON.parse(userJson);
      if (user.l_usuario) return user.l_usuario;
    }
    return 'system';
  }
}
